import React, { useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, createBrowserRouter, RouterProvider } from "react-router-dom";
import { Layout } from "./components/Layout";
import Index from "./pages/Index";
import Settings from "./pages/Settings";
import UserSettings from "./pages/UserSettings";
import Calendar from "./pages/Calendar";
import Articles from "./pages/Articles";
import Videos from "./pages/Videos";
import AuthPage from "./pages/Auth";
import { supabase } from "./integrations/supabase/client";
import { Session } from "@supabase/supabase-js";
import { toast } from "sonner";
import Intercom from '@intercom/messenger-js-sdk';
import { usePostHog, useFeatureFlagEnabled } from 'posthog-js/react'
import { useLinkedInTokenMonitor } from '@/hooks/useLinkedInTokenMonitor';
import { LinkedInReauthorizeModal } from '@/components/LinkedInReAuthModal';
import { useModal } from '@/contexts/ModalContext';
import { ModalProvider } from '@/contexts/ModalContext';
import { useIsMobile } from "@/hooks/use-mobile";

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const posthog = usePostHog()

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error("Error fetching session:", error);
        toast.error("Session error. Please login again.");
        setSession(null);
      } else {
        setSession(session);
        if (session?.user) {
          // Identify user in PostHog FIRST
          posthog?.identify(session.user.id, {
            email: session.user.email,
            created_at: session.user.created_at,
          })
          
          // Then initialize Intercom
          Intercom({
            app_id: 'ga2n2nbk',
            user_id: session.user.id,
            email: session.user.email,
            created_at: Math.floor(new Date(session.user.created_at).getTime() / 1000),
          });

          // After posthog?.identify
          posthog?.capture('user_authenticated');
        }
      }
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (_event === 'SIGNED_OUT') {
        // Reset PostHog user FIRST
        posthog?.reset()
        toast.info("You have been signed out");
        // Then shutdown Intercom
        Intercom({ app_id: 'ga2n2nbk', shutdown: true });
      } else if (_event === 'SIGNED_IN' && session?.user) {
        // Identify user in PostHog FIRST
        posthog?.identify(session.user.id, {
          email: session.user.email,
          created_at: session.user.created_at,
        })
        
        // Then initialize Intercom
        Intercom({
          app_id: 'ga2n2nbk',
          user_id: session.user.id,
          email: session.user.email,
          created_at: Math.floor(new Date(session.user.created_at).getTime() / 1000),
        });

        // After posthog?.identify
        posthog?.capture('user_authenticated');
      }
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, [posthog]);

  // Show loading state
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  // Redirect to auth page if no session
  if (!session) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
};

const MobilePlaceholder = () => {
  return (
    <div className="min-h-screen">
      <div className="p-4">
        <img 
          src="/lovable-uploads/b395a010-3cbe-4756-9e56-02d87a12853b.png" 
          alt="LinkPilot Logo" 
          className="h-8 w-auto"
        />
      </div>
      <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center p-4">
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-semibold">Not so fast...</h2>
          <p className="text-muted-foreground">LinkPilot is not optimised for mobile yet. Please navigate to https://app.linkpilot.pro on your desktop to use the application.</p>
        </div>
      </div>
    </div>
  );
};

const AppContent = () => {
  const isMobile = useIsMobile();
  const showVideosPage = useFeatureFlagEnabled('show-linkpilot-videos')
  const showArticlesPage = useFeatureFlagEnabled('show-linkpilot-articles')
  const { isTokenExpired, initiateReauthorization } = useLinkedInTokenMonitor();
  const { openModal, isOpen, closeModal, modalData } = useModal();

  useEffect(() => {
    if (isTokenExpired) {
      openModal({
        type: 'linkedin-reauth',
        onReauthorize: initiateReauthorization
      });
    }
  }, [isTokenExpired]);

  if (isMobile) {
    return <MobilePlaceholder />;
  }

  return (
    <BrowserRouter>
      <TooltipProvider>
        <LinkedInReauthorizeModal
          isOpen={isOpen}
          onClose={closeModal}
          onReauthorize={initiateReauthorization}
          isInitialSetup={modalData?.isInitialSetup}
        />
        <Toaster />
        <Sonner />
        <Routes>
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/" element={
            <ProtectedRoute>
              <Layout><Index /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <Layout><Settings /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/user-settings" element={
            <ProtectedRoute>
              <Layout><UserSettings /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/calendar" element={
            <ProtectedRoute>
              <Layout><Calendar /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/articles" element={
            <ProtectedRoute>
              <Layout><Articles /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/videos" element={
            <ProtectedRoute>
              <Layout><Videos /></Layout>
            </ProtectedRoute>
          } />
        </Routes>
      </TooltipProvider>
    </BrowserRouter>
  );
};

const App = () => {
  const queryClient = React.useState(() => new QueryClient())[0];

  // Initialize Intercom when app loads
  useEffect(() => {
    Intercom({
      app_id: 'ga2n2nbk',
    });
  }, []);

  return (
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <ModalProvider>
          <AppContent />
        </ModalProvider>
      </QueryClientProvider>
    </React.StrictMode>
  );
};

export default App;